import { Box, Typography, IconButton, alpha, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GlassCard } from '@paneon/ui';
import { useTheme } from '@paneon/theme';
import type { Pet } from '../services/api';

interface PetCardProps {
  pet: Pet;
  onEdit?: (pet: Pet) => void;
  onDelete?: (pet: Pet) => void;
  onClick?: (pet: Pet) => void;
}

const speciesEmojis: Record<string, string> = {
  dog: '🐕',
  cat: '🐱',
  bird: '🐦',
  fish: '🐟',
  rabbit: '🐰',
  hamster: '🐹',
  other: '🐾',
};

const speciesNames: Record<string, string> = {
  dog: 'Hund',
  cat: 'Katze',
  bird: 'Vogel',
  fish: 'Fisch',
  rabbit: 'Kaninchen',
  hamster: 'Hamster',
  other: 'Sonstiges',
};

export function PetCard({ pet, onEdit, onDelete, onClick }: PetCardProps) {
  const { isDarkMode } = useTheme();

  const emoji = speciesEmojis[pet.species] || speciesEmojis.other;
  const speciesName = speciesNames[pet.species] || pet.species;

  // Calculate age if birth_date is available
  const getAge = () => {
    if (!pet.birth_date) return null;
    const birth = new Date(pet.birth_date);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();

    if (years < 1) {
      return `${months + years * 12} Monate`;
    }
    return `${years} Jahr${years > 1 ? 'e' : ''}`;
  };

  const age = getAge();

  return (
    <GlassCard
      sx={{
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 32px ${alpha(isDarkMode ? '#000' : '#4CAF50', 0.2)}`,
            }
          : {},
      }}
      onClick={() => onClick?.(pet)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Pet Photo or Emoji */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: pet.photo_url ? undefined : 32,
            backgroundColor: alpha(isDarkMode ? '#fff' : '#4CAF50', 0.1),
            overflow: 'hidden',
          }}
        >
          {pet.photo_url ? (
            <img
              src={pet.photo_url}
              alt={pet.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            emoji
          )}
        </Box>

        {/* Pet Info */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#fff' : '#000',
              lineHeight: 1.2,
            }}
          >
            {pet.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={speciesName}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                backgroundColor: alpha(isDarkMode ? '#fff' : '#4CAF50', 0.15),
                color: isDarkMode ? '#fff' : '#4CAF50',
              }}
            />
            {pet.breed && (
              <Chip
                label={pet.breed}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: 11,
                  borderColor: alpha(isDarkMode ? '#fff' : '#000', 0.2),
                  color: isDarkMode ? alpha('#fff', 0.7) : alpha('#000', 0.7),
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            {age && (
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6) }}
              >
                {age}
              </Typography>
            )}
            {pet.weight_kg && (
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6) }}
              >
                {pet.weight_kg} kg
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onEdit && (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onEdit(pet);
              }}
              sx={{
                color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                '&:hover': { color: '#4CAF50' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onDelete(pet);
              }}
              sx={{
                color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                '&:hover': { color: '#f44336' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </GlassCard>
  );
}
